// ======================================================================
// AUTH LOCKED — NAO MODIFICAR SEM APROVACAO EXPLICITA DO USUARIO
// Alteracoes aqui podem quebrar login em producao.
// Regra: Se alguma tarefa envolver login/auth, NAO implementar
// automaticamente. Explicar o impacto e aguardar aprovacao.
// ======================================================================
import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API_URL = window.location.origin + '/api';

// Build-time stamp for cache diagnostics (changes every build)
const AUTH_BUILD_STAMP = Date.now().toString(36);

const DEFAULT_PERMISSIONS_BY_ROLE = {
  'ADMIN': {
    can_create_call: true, can_create_report: true, can_validate_report: true,
    can_assign_work_order: true, can_manage_departments: true, can_finance_pay: true,
    can_view_ceo: true, can_view_my_os: true, can_view_dashboard: true,
    can_view_sector_queue: true, can_manage_materials: true, can_configure_bonus: true,
    can_manage_redemptions: true, can_view_my_panel: true, can_view_my_coins: true, can_manage_patents: true,
    can_view_purchase_requests: true, can_manage_purchase_requests: true, can_manage_stock: true,
    can_manage_whatsapp_templates: true, can_view_whatsapp_logs: true,
    can_manage_tools: true, can_manage_quotations: true, can_manage_suppliers: true,
    can_manage_users: true, can_manage_settings: true, can_request_material: true,
    can_manage_work_schedule: true,
  },
  'GESTOR': {
    can_create_call: true, can_create_report: true, can_validate_report: true,
    can_assign_work_order: true, can_manage_departments: true, can_finance_pay: false,
    can_view_ceo: true, can_view_my_os: true, can_view_dashboard: true,
    can_view_sector_queue: true, can_manage_materials: true, can_configure_bonus: true,
    can_manage_redemptions: true, can_view_my_panel: true, can_view_my_coins: true, can_manage_patents: true,
    can_view_purchase_requests: true, can_manage_purchase_requests: true, can_manage_stock: true,
    can_manage_whatsapp_templates: true, can_view_whatsapp_logs: true,
    can_manage_tools: true, can_manage_quotations: true, can_manage_suppliers: true,
    can_manage_users: false, can_manage_settings: false, can_request_material: true,
    can_manage_work_schedule: true,
  },
  'MANAGER': {
    can_create_call: true, can_create_report: true, can_validate_report: true,
    can_assign_work_order: true, can_manage_departments: true, can_finance_pay: false,
    can_view_ceo: true, can_view_my_os: true, can_view_dashboard: true,
    can_view_sector_queue: true, can_manage_materials: true, can_configure_bonus: true,
    can_manage_redemptions: true, can_view_my_panel: true, can_view_my_coins: true, can_manage_patents: true,
    can_view_purchase_requests: true, can_manage_purchase_requests: true, can_manage_stock: true,
    can_manage_whatsapp_templates: true, can_view_whatsapp_logs: true,
    can_manage_tools: true, can_manage_quotations: true, can_manage_suppliers: true,
    can_manage_users: false, can_manage_settings: false, can_request_material: true,
    can_manage_work_schedule: true,
  },
  'RECEPCAO': {
    can_create_call: true, can_create_report: true, can_validate_report: false,
    can_assign_work_order: false, can_manage_departments: false, can_finance_pay: false,
    can_view_ceo: false, can_view_my_os: false, can_view_dashboard: true,
    can_view_sector_queue: false, can_manage_materials: false, can_configure_bonus: false,
    can_manage_redemptions: false, can_view_my_panel: false, can_view_my_coins: true, can_manage_patents: false,
    can_view_purchase_requests: false, can_manage_purchase_requests: false, can_manage_stock: false,
    can_manage_whatsapp_templates: false, can_view_whatsapp_logs: false,
    can_manage_tools: false, can_manage_quotations: false, can_manage_suppliers: false,
    can_manage_users: false, can_manage_settings: false, can_request_material: true,
    can_manage_work_schedule: false,
  },
  'MANUTENCAO': {
    can_create_call: false, can_create_report: true, can_validate_report: false,
    can_assign_work_order: false, can_manage_departments: false, can_finance_pay: false,
    can_view_ceo: false, can_view_my_os: true, can_view_dashboard: true,
    can_view_sector_queue: false, can_manage_materials: false, can_configure_bonus: false,
    can_manage_redemptions: true, can_view_my_panel: true, can_view_my_coins: true, can_manage_patents: false,
    can_view_purchase_requests: false, can_manage_purchase_requests: false, can_manage_stock: false,
    can_manage_whatsapp_templates: false, can_view_whatsapp_logs: false,
    can_manage_tools: false, can_manage_quotations: false, can_manage_suppliers: false,
    can_manage_users: false, can_manage_settings: false, can_request_material: true,
    can_manage_work_schedule: false,
  },
  'FINANCEIRO': {
    can_create_call: false, can_create_report: false, can_validate_report: false,
    can_assign_work_order: false, can_manage_departments: false, can_finance_pay: true,
    can_view_ceo: false, can_view_my_os: false, can_view_dashboard: true,
    can_view_sector_queue: false, can_manage_materials: false, can_configure_bonus: false,
    can_manage_redemptions: false, can_view_my_panel: false, can_view_my_coins: false, can_manage_patents: false,
    can_view_purchase_requests: true, can_manage_purchase_requests: false, can_manage_stock: false,
    can_manage_whatsapp_templates: false, can_view_whatsapp_logs: false,
    can_manage_tools: false, can_manage_quotations: false, can_manage_suppliers: false,
    can_manage_users: false, can_manage_settings: false, can_request_material: false,
    can_manage_work_schedule: false,
  },
  'COLABORADOR': {
    can_create_call: false, can_create_report: true, can_validate_report: false,
    can_assign_work_order: false, can_manage_departments: false, can_finance_pay: false,
    can_view_ceo: false, can_view_my_os: false, can_view_dashboard: true,
    can_view_sector_queue: false, can_manage_materials: false, can_configure_bonus: false,
    can_manage_redemptions: true, can_view_my_panel: false, can_view_my_coins: true, can_manage_patents: false,
    can_view_purchase_requests: false, can_manage_purchase_requests: false, can_manage_stock: false,
    can_manage_whatsapp_templates: false, can_view_whatsapp_logs: false,
    can_manage_tools: false, can_manage_quotations: false, can_manage_suppliers: false,
    can_manage_users: false, can_manage_settings: false, can_request_material: false,
    can_manage_work_schedule: false,
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [tenantSlug, setTenantSlug] = useState(localStorage.getItem('tenant_slug') || null);

  useEffect(() => {
    if (token) {
      loadUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const getAuthHeaders = () => {
    const headers = { 'Authorization': `Bearer ${token}` };
    // Extract tenant slug from current URL path (takes priority over stored slug)
    const pathMatch = window.location.pathname.match(/^\/t\/([^/]+)/);
    const currentSlug = pathMatch ? pathMatch[1] : tenantSlug;
    if (currentSlug) headers['X-Tenant-Slug'] = currentSlug;
    return headers;
  };

  const loadUser = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      if (tenantSlug) headers['X-Tenant-Slug'] = tenantSlug;
      const response = await axios.get(`${API_URL}/auth/me`, { headers });
      const userData = response.data;
      if (userData.company_slug) {
        setTenantSlug(userData.company_slug);
        localStorage.setItem('tenant_slug', userData.company_slug);
      }
      setUser(userData);
      await loadPermissions(userData);
    } catch (error) {
      localStorage.removeItem('token');
      localStorage.removeItem('tenant_slug');
      setToken(null);
      setTenantSlug(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const loadPermissions = async (userData) => {
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      if (tenantSlug) headers['X-Tenant-Slug'] = tenantSlug;
      const response = await axios.get(`${API_URL}/auth/permissions`, { headers });
      setPermissions(response.data || {});
    } catch {
      const role = (userData?.role || '').toUpperCase();
      setPermissions(DEFAULT_PERMISSIONS_BY_ROLE[role] || DEFAULT_PERMISSIONS_BY_ROLE['COLABORADOR']);
    }
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    if ((user.role || '').toUpperCase() === 'ADMIN') return true;
    return permissions[permission] === true;
  };

  const login = async (email, password) => {
    // Step-by-step login with explicit diagnostics at each stage
    console.info(`[AUTH] Login attempt | API: ${API_URL} | Origin: ${window.location.origin} | Build: ${AUTH_BUILD_STAMP}`);

    // STEP 1: Send login request
    let response;
    try {
      response = await axios.post(`${API_URL}/auth/login`, { email, password });
    } catch (err) {
      // Enrich error with exact step info for Login.js handler
      err._authStep = 'AUTH_REQUEST_FAILED';
      err._authDetail = `POST ${API_URL}/auth/login failed: ${err.response?.status || 'NETWORK'} - ${err.response?.data?.detail || err.message}`;
      console.error(`[AUTH] STEP 1 FAILED: ${err._authDetail}`);
      throw err;
    }
    console.info('[AUTH] STEP 1 OK: Login request returned 200');

    // STEP 2: Extract token and profile from response
    const newToken = response.data?.token;
    const profile = response.data?.profile;
    if (!newToken) {
      const err = new Error('AUTH_INVALID_RESPONSE: Server returned 200 but no token');
      err._authStep = 'AUTH_INVALID_RESPONSE';
      err._authDetail = 'Login response missing token field';
      console.error('[AUTH] STEP 2 FAILED:', err._authDetail, response.data);
      throw err;
    }
    console.info('[AUTH] STEP 2 OK: Token and profile extracted');

    // STEP 3: Persist token
    try {
      localStorage.setItem('token', newToken);
      setToken(newToken);
    } catch (err) {
      err._authStep = 'AUTH_SESSION_CREATE_FAILED';
      err._authDetail = 'Failed to persist token in localStorage';
      console.error('[AUTH] STEP 3 FAILED:', err._authDetail);
      throw err;
    }
    console.info('[AUTH] STEP 3 OK: Token stored');

    // STEP 4: Store tenant slug
    if (profile?.company_slug) {
      setTenantSlug(profile.company_slug);
      localStorage.setItem('tenant_slug', profile.company_slug);
    }
    console.info('[AUTH] STEP 4 OK: Tenant slug stored:', profile?.company_slug || 'none');

    // STEP 5: Set user and load permissions
    if (profile) {
      setUser(profile);
      const headers = { 'Authorization': `Bearer ${newToken}` };
      if (profile.company_slug) headers['X-Tenant-Slug'] = profile.company_slug;
      try {
        const permResponse = await axios.get(`${API_URL}/auth/permissions`, { headers });
        setPermissions(permResponse.data || {});
        console.info('[AUTH] STEP 5 OK: Permissions loaded from server');
      } catch (permErr) {
        console.warn('[AUTH] STEP 5 WARN: Permission load failed, using defaults:', permErr.message);
        const role = (profile.role || '').toUpperCase();
        setPermissions(DEFAULT_PERMISSIONS_BY_ROLE[role] || DEFAULT_PERMISSIONS_BY_ROLE['COLABORADOR']);
      }
    }

    console.info('[AUTH] LOGIN COMPLETE: All steps passed');
    return response.data;
  };

  const register = async (email, password, fullName, companyName, role) => {
    const response = await axios.post(`${API_URL}/auth/register`, {
      email, password, full_name: fullName, company_name: companyName, role
    });
    const newToken = response.data.token;
    const profile = response.data.profile;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    if (profile?.company_slug) {
      setTenantSlug(profile.company_slug);
      localStorage.setItem('tenant_slug', profile.company_slug);
    }
    if (profile) {
      setUser(profile);
    }
    return response.data;
  };

  const setAuthData = async (newToken, profile) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    if (profile?.company_slug) {
      setTenantSlug(profile.company_slug);
      localStorage.setItem('tenant_slug', profile.company_slug);
    }
    if (profile) {
      setUser(profile);
      const headers = { 'Authorization': `Bearer ${newToken}` };
      if (profile.company_slug) headers['X-Tenant-Slug'] = profile.company_slug;
      try {
        const permResponse = await axios.get(`${API_URL}/auth/permissions`, { headers });
        setPermissions(permResponse.data || {});
      } catch {
        const role = (profile.role || '').toUpperCase();
        setPermissions(DEFAULT_PERMISSIONS_BY_ROLE[role] || DEFAULT_PERMISSIONS_BY_ROLE['COLABORADOR']);
      }
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('tenant_slug');
    sessionStorage.clear();
    setToken(null);
    setTenantSlug(null);
    setUser(null);
    setPermissions({});
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading, permissions, tenantSlug,
      login, register, logout, getAuthHeaders, hasPermission, setAuthData
    }}>
      {children}
    </AuthContext.Provider>
  );
};


/**
 * Hook to get tenant-prefixed path.
 * Usage: const tp = useTenantPath(); navigate(tp('/dashboard'));
 */
export const useTenantPath = () => {
  const { tenantSlug } = useAuth();
  return (path) => tenantSlug ? `/t/${tenantSlug}${path}` : path;
};
